import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  input,
  output,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, from, of, startWith, Subject, switchMap, take, takeUntil } from 'rxjs';
import { ModalController, Platform } from '@ionic/angular/standalone';
import { getModalConfig, shouldShowAsSheetModal } from 'src/app/core/services/modal/modal.utils';

import { WaitingOpponentsData } from 'src/app/core/api/player/schemas/waiting-opponents';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { TiersService } from 'src/app/core/services/tiers/tiers.service';
import { SoloTiersService } from 'src/app/core/services/tiers/solo-tiers.service';
import { WaitingOpponentsService } from 'src/app/core/services/waiting-opponents/waiting-opponents.service';
import { GameId } from 'src/app/shared/models/game/game-id';
import { ContinueGameDrawerComponent } from '../../continue-game-drawer/continue-game-drawer.component';
import { WagerTierListComponent } from 'src/app/features/wager/components/wager-tier-list/wager-tier-list.component';
import { GameControlDisplay } from '../../services/game-ui/game-control-display';
import { TournamentService } from 'src/app/core/services/tournament/tournament.service';
import { TournamentDataModel } from 'src/app/core/services/tournament/models/tournament-data-model';
import { CountdownPillComponent } from 'src/app/ui/pills/countdown-pill/countdown-pill.component';
import { ButtonComponent } from 'src/app/ui/buttons/button/button.component';
import { TranslocoService } from '@jsverse/transloco';
import { createUnfinishedCountdown } from 'src/app/core/services/rooms-results/unfinished-countdown';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { GameTutorialService } from 'src/app/features/tutorials/services/game-tutorial.service';
import { SoloSheetService } from 'src/app/features/solo/services/solo-sheet.service';
import { PaymentService } from 'src/app/features/payments/services/payment.service';
import { RoomsResultsService } from 'src/app/core/services/rooms-results/rooms-results.service';
import { GameOrchestrationService } from '../../services/game-orchestration.service';
import {
  TierPlayEventData,
  DuelPaymentEventData,
  FreeMatchPlayEventData,
  TournamentPaymentEventData,
} from 'src/app/core/services/tiers/models/payment-events';

@Component({
  selector: 'app-tier-first-controls',
  templateUrl: './tier-first-controls.component.html',
  styleUrls: ['./tier-first-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [WagerTierListComponent, CountdownPillComponent, ButtonComponent],
})
export class TierFirstControlsComponent
  implements OnInit, OnDestroy, AfterViewInit, GameControlDisplay
{
  readonly gameId = input.required<GameId>();
  readonly autoOpenSoloSheet = input(false);

  readonly showPaymentEvent = output<TierPlayEventData>();
  readonly launchPracticeEvent = output<void>();
  readonly launchLocalEvent = output<void>();

  private tiersService = inject(TiersService);
  private soloTiersService = inject(SoloTiersService);
  private soloSheetService = inject(SoloSheetService);
  private paymentService = inject(PaymentService);
  private waitingOpponentsService = inject(WaitingOpponentsService);
  private roomsResultsService = inject(RoomsResultsService);
  private tournamentService = inject(TournamentService);
  private analyticsService = inject(AnalyticsService);
  private operatorService = inject(OperatorService);
  private modalController = inject(ModalController);
  private platform = inject(Platform);
  private translocoService = inject(TranslocoService);
  private themeService = inject(ThemeService);
  private gameTutorialService = inject(GameTutorialService);
  private orchestration = inject(GameOrchestrationService);

  private destroy$ = new Subject<void>();
  private currentTournamentData = signal<TournamentDataModel | undefined>(undefined);

  readonly waitingOpponentsStandard = signal<WaitingOpponentsData | undefined>(undefined);
  readonly allTiersSignal = computed(() => this.tiersService.duelTiers() ?? []);
  readonly freeMatchesSignal = this.tiersService.freeMatches;
  readonly soloTiersSignal = this.soloTiersService.soloTiers;
  readonly paymentInFlight = this.paymentService.paymentInFlight;

  readonly unfinishedRoomInitialised = this.roomsResultsService.initialised;
  readonly hasUnfinishedRoom = this.roomsResultsService.hasUnfinishedRoom;

  /** The wager list's overlay is showing (not the continue-game footer). */
  readonly showsOverlay = computed(
    () => this.unfinishedRoomInitialised() && !this.hasUnfinishedRoom(),
  );
  /** The overlay is see-through, so the app background shows behind the cards. */
  readonly showsAppBackground = this.themeService.playTabConfig().tierFirstShowsAppBackground;

  // Tells the app shell when the overlay covers the game, so a see-through overlay can hide it.
  private readonly overlayOpenEffect = effect(() => {
    this.orchestration.setTierFirstOverlayOpen(this, this.showsOverlay());
  });

  private initialised$ = toObservable(this.roomsResultsService.initialised);

  readonly unfinishedTimeRemaining = createUnfinishedCountdown(
    this.roomsResultsService,
    this.translocoService,
  );

  readonly showingControls = signal(true);

  ngOnInit(): void {
    this.tournamentService.refreshCurrentTournament();

    this.tournamentService.currentTournamentData$
      .pipe(
        startWith(this.tournamentService.currentTournamentSnapshot),
        switchMap((currentTournamentData) => {
          this.currentTournamentData.set(currentTournamentData);
          if (!this.themeService.hasWaitingOpponents()) {
            return of(undefined);
          }
          return from(
            this.waitingOpponentsService.fetchWaitingOpponents(
              this.gameId(),
              currentTournamentData?.tournamentId,
            ),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((waitingOpponents) => {
        this.waitingOpponentsStandard.set(waitingOpponents);
      });

    this.operatorService.updateBalance(OperatorService.IsDemo);

    if (this.autoOpenSoloSheet()) {
      this.openSoloSheet();
    }
  }

  get currentTournamentDataSignal(): Signal<TournamentDataModel | undefined> {
    return this.currentTournamentData.asReadonly();
  }

  readonly continueButtonLabel = computed(() => this.getTranslation('continue-game'));

  ngAfterViewInit(): void {
    this.initialised$
      .pipe(
        filter((initialised) => initialised),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(async () => {
        if (!this.roomsResultsService.hasUnfinishedRoom()) {
          // The tier list is about to show on the first page: refresh grants and Solo tiers the
          // same way the drawer does on open. A failed grants refresh leaves the initial fetch's
          // tiles in place; a failed Solo refresh empties its list until the next open.
          void this.tiersService.refreshFreeMatches().catch(() => undefined);
          void this.soloTiersService.refresh();
          await this.gameTutorialService.tryShowTutorial(this.gameId());
          return;
        }

        const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
        const continueGameModal = await this.modalController.create({
          component: ContinueGameDrawerComponent,
          ...getModalConfig(asSheetModal, { mobile: 'custom-modal-height' }),
        });
        await continueGameModal.present();
        this.analyticsService.log(EventName.ContinueGamePopupShow);
      });
  }

  ngOnDestroy(): void {
    this.orchestration.setTierFirstOverlayOpen(this, false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  onWagerClick(data: DuelPaymentEventData): void {
    this.analyticsService.log(EventName.SelectTier, { tierId: data.tierData.tierId });
    this.showPaymentEvent.emit(data);
  }

  onFreeMatchClick(data: FreeMatchPlayEventData): void {
    // Same event as a paid tap so tier-selection funnels stay whole; the extra properties are
    // what separates a free selection from a paid one downstream.
    this.analyticsService.log(EventName.SelectTier, {
      tierId: data.grant.tierId,
      freeMatch: true,
      grantId: data.grant.grantId,
    });
    this.showPaymentEvent.emit(data);
  }

  async onSoloClick(): Promise<void> {
    await this.openSoloSheet();
  }

  // The sheet pays for itself, so nothing is emitted: the game launches from inside it.
  async openSoloSheet(): Promise<void> {
    await this.soloSheetService.open(this.gameId());
  }

  async onTournamentPlayClick(data: TournamentPaymentEventData): Promise<void> {
    this.analyticsService.log(EventName.TournamentSelectTier, {
      tierId: data.tournamentData.tierId,
      tournamentId: data.tournamentData.tournamentId,
    });
    this.showPaymentEvent.emit(data);
  }

  onPracticeClick(): void {
    this.analyticsService.log(EventName.PracticeButtonClicked);
    this.launchPracticeEvent.emit();
  }

  onLocalClick(): void {
    this.launchLocalEvent.emit();
  }

  async continueGameClicked(): Promise<void> {
    const gameContinued = await this.roomsResultsService.launchUnfinishedGame();
    if (gameContinued) {
      this.analyticsService.log(EventName.ContinueGameScreenLaunchGame);
    }
  }

  private getTranslation(key: string): string {
    return this.translocoService.translate(`components.loading-game-controls.${key}`);
  }
}
